/**
 * AccountHintGuard — catches "this email was about my OTHER account".
 *
 * Fias is used with a work account and a personal account in the same browser,
 * so a security notice ("two-factor was turned off") can be opened while signed
 * in as a different account. Rendering the wrong settings page silently is
 * worse than it sounds: the reader sees MFA *on*, concludes the email was
 * phishing, and dismisses the exact warning it existed to deliver.
 *
 * Security emails therefore carry an account hint, and this guard compares it
 * against the authenticated session before the page renders.
 *
 * WHY A FRAGMENT (`#a=…`), NOT A QUERY PARAM
 * Fragments are never transmitted to the server, so the hint never reaches
 * access logs, referrer headers, or request telemetry. It is also the opaque
 * internal user id, never an email address — an address in a URL would land in
 * browser history on a shared machine.
 *
 * WHY THE INTERSTITIAL NEVER NAMES THE HINTED ACCOUNT
 * The hint arrives as attacker-controllable input in a URL. Resolving it to a
 * username or email and displaying it would turn this page into an identity
 * oracle: paste a guessed id, see whose it is. So the copy names only the
 * CURRENT user — whom the session already authenticates — and says nothing
 * about the account the link was for. The hint carries no authority; it is
 * compared and discarded.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { UserRoundX } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/** Fragment key carrying the opaque account hint. Mirrored in email-service.ts. */
const HINT_KEY = 'a';

/** Read the hint from `location.hash` (pure — no URL mutation). */
function readAccountHint(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = window.location.hash.replace(/^#/, '');
  if (!raw) return null;
  return new URLSearchParams(raw).get(HINT_KEY);
}

/**
 * Strip the hint from the URL.
 *
 * Always strips, match or not: the hint is single-use, and leaving it in the
 * address bar would re-trigger the check on refresh and follow the URL into
 * bookmarks and shared links.
 */
function stripAccountHintFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const raw = url.hash.replace(/^#/, '');
    if (!raw) return;
    const params = new URLSearchParams(raw);
    if (!params.has(HINT_KEY)) return;
    // Remove ONLY the `a` hint — an unrelated fragment param (e.g. #section=…)
    // that happened to travel alongside it must survive.
    params.delete(HINT_KEY);
    const rest = params.toString();
    url.hash = rest ? `#${rest}` : '';
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Non-fatal — the comparison already ran off the synchronous read.
  }
}

export function AccountHintGuard({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  // Read the hint SYNCHRONOUSLY on first render so the guard decides correctly
  // on the first paint — the guarded page never mounts (and never fires its
  // queries) for a render before an effect resolves. `null` = no hint present.
  // The URL strip is a separate mount effect; it's idempotent, so StrictMode's
  // double-invoke is harmless, and the read above already has what it needs.
  const [hint] = useState<string | null>(() => readAccountHint());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Strip ONLY when we actually consumed an `a` hint — an unrelated fragment
    // (e.g. #section=security) must be left in place, not silently removed.
    if (hint != null) stripAccountHintFromUrl();
  }, [hint]);

  // Only a hint that is present AND disagrees with the session blocks the page.
  // An absent hint (direct visit, older email) renders normally.
  const mismatch = hint != null && user?.userId != null && hint !== user.userId;

  if (!mismatch || dismissed) return <>{children}</>;

  return (
    <div className="max-w-2xl mx-auto py-10 px-4">
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <UserRoundX className="h-6 w-6 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <CardTitle>This link is for a different account</CardTitle>
              <CardDescription>
                You opened a security link for another Fias account, but you’re currently signed in
                {user?.username ? ' as ' : ''}
                {user?.username ? <strong>{user.username}</strong> : ''}.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Settings shown here apply to the account you’re signed in as. To act on the account the
            email was about, sign out and sign back in as that account.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void signOut()}>Sign out and switch account</Button>
            <Button variant="outline" onClick={() => setDismissed(true)}>
              Continue as {user?.username || 'current user'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
